import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanDeactivateFn, RouterStateSnapshot } from '@angular/router';
import { ApplicationPath } from '@core/navigation/models/application-path.enum';
import { IpAccessControlFacade } from '@pages/security/facades/ip-access-control.facade';
import { Observable, combineLatest, finalize, first, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { SaveChangesModalComponent } from '@pages/security/components/save-changes-modal/save-changes-modal.component';
import { SecurityPageComponent } from '@pages/security/components/security-page/security-page.component';
import { DialogService } from '@kaseya-design-components';

let hasModalResult$: Observable<boolean> | undefined;

export const hasIpAccessUnsavedChangesGuard: CanDeactivateFn<SecurityPageComponent> = (
  _component: SecurityPageComponent,
  _currentRoute: ActivatedRouteSnapshot,
  _currentState: RouterStateSnapshot,
  nextState?: RouterStateSnapshot
): Observable<boolean> => {
  const ipAccessControlFacade = inject(IpAccessControlFacade);
  const dialogService = inject(DialogService);

  if (isRefreshTokenExpired(nextState)) {
    return of(true);
  }

  if (dialogService.hasOpenDialogs && hasModalResult$) {
    return hasModalResult$;
  }

  hasModalResult$ = shouldSkipConfirmationModal(ipAccessControlFacade).pipe(
    first(),
    switchMap(shouldSkip => (shouldSkip ? of(true) : isDialogNotConfirmed(dialogService, ipAccessControlFacade))),
    shareReplay(),
    finalize(() => (hasModalResult$ = undefined))
  );

  return hasModalResult$;
};

const shouldSkipConfirmationModal = ({
  isIpAddressesListEmpty$,
  areIpAddressesEnabled$,
  isListModified$,
}: IpAccessControlFacade): Observable<boolean> =>
  combineLatest([isIpAddressesListEmpty$, areIpAddressesEnabled$, isListModified$]).pipe(
    map(
      ([isIpAddressesListEmpty, areIpAddressesEnabled, isListModified]) =>
        areIpAddressesEnabled || isIpAddressesListEmpty || !isListModified
    )
  );

const isDialogNotConfirmed = (dialogService: DialogService, ipAccessControlFacade: IpAccessControlFacade): Observable<boolean> => {
  const modalRef = dialogService.openDialog(SaveChangesModalComponent);
  return modalRef.onClose().pipe(
    tap(() => ipAccessControlFacade.markIpAddressesListAsNotModified()),
    map(({ confirmed }) => !confirmed)
  );
};

const isRefreshTokenExpired = (nextState?: RouterStateSnapshot): boolean => nextState?.url === `/${ApplicationPath.Login}`;
